import React, { Component, Suspense } from 'react'
import { HashRouter, Route, Routes, Outlet, Navigate } from 'react-router-dom'
import axios from 'axios'
import './scss/style.scss'

import HotAxleAlert from './components/HotaxleAlert'
import VersionChecker from './components/VersionChecker'
import ZoneGuard from './utils/zoneGuard'
import { debugLog } from './utils/debugLog'
import { clearAuth } from './utils/authInterceptor'

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)

// Containers
// const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Division = React.lazy(() => import('./views/pages/axle/Division'))
const TempSetting = React.lazy(() => import('./views/pages/axle/TempSetting'))
const UserSetting = React.lazy(() => import('./views/pages/axle/UserSetting'))
const Station = React.lazy(() => import('./views/pages/axle/Station'))
const SmsAlert = React.lazy(() => import('./views/pages/axle/SmsAlert'))
const Device = React.lazy(() => import('./views/pages/axle/Device'))
const AlertHistory = React.lazy(() => import('./views/pages/axle/AlertHistory'))
const Zone = React.lazy(() => import('./views/pages/axle/Zone'))
const AlertColorConfig = React.lazy(() => import('./views/pages/axle/AlertColorConfig'))
const HabdHistory = React.lazy(() => import('./views/pages/axle/HabdHistory'))
const DeviceReading = React.lazy(() => import('./views/pages/axle/DeviceReading'))
const DeviceReadingDevice = React.lazy(() => import('./views/pages/axle/DeviceReadingDevice'))
const SearchTable2 = React.lazy(() => import('./views/pages/axle/SearchTable2'))
const DeviceStatus = React.lazy(() => import('./views/pages/axle/DeviceStatus'))
const Comparability = React.lazy(() => import('./views/pages/axle/Compatibility'))
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))
const Maintenance = React.lazy(()=>import("./views/pages/axle/Maintenance"))
const DeviceDetails = React.lazy(()=>import("./views/pages/axle/DeviceDetails"))
const DeviceHealth = React.lazy(()=>import("./views/pages/axle/DeviceHealth"))
// Super-admin only pages: routed here but NOT listed in _nav.js, so ZoneGuard admits only zone _ALL.
const BreakdownReport = React.lazy(() => import('./views/pages/axle/BreakdownReport'))
const AlignmentRecommend = React.lazy(() => import('./views/pages/axle/AlignmentRecommend'))
const RuleEngine = React.lazy(()=>import("./views/pages/axle/RuleEngine"))
const Device2 = React.lazy(() => import('./views/pages/axle/Device2'))
const ReadingCount = React.lazy(() => import('./views/pages/axle/ReadingCount'))
const MapView = React.lazy(() => import('./views/pages/axle/MapView'))
const Contact = React.lazy(() => import('./views/pages/axle/Contact'))
const StatusHistory = React.lazy(() => import('./views/pages/axle/StatusHistory'))
const DeviceFault = React.lazy(() => import('./views/pages/axle/DeviceFault'))
const DeviceCommand = React.lazy(() => import('./views/pages/axle/DeviceCommand'))
const HabdDetails = React.lazy(() => import('./views/pages/axle/HabdDetails'))
const TicketsIndex = React.lazy(() => import('./views/pages/axle/tickets/TicketsIndex'))
const TicketCreate = React.lazy(() => import('./views/pages/axle/tickets/TicketCreate'))
const TicketDetail = React.lazy(() => import('./views/pages/axle/tickets/TicketDetail'))
const TicketEdit = React.lazy(() => import('./views/pages/axle/tickets/TicketEdit'))
const TicketAnalyticsLayout = React.lazy(() =>
  import('./views/pages/axle/tickets/analytics/AnalyticsLayout'),
)
const TicketSymptomsReport = React.lazy(() =>
  import('./views/pages/axle/tickets/analytics/SymptomsReport'),
)
const TicketPartsReport = React.lazy(() =>
  import('./views/pages/axle/tickets/analytics/PartsReport'),
)
const TicketWorkEffectivenessReport = React.lazy(() =>
  import('./views/pages/axle/tickets/analytics/WorkEffectivenessReport'),
)
const setAuthToken = (token) => {
   if (token) {
       axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
   }
   else
       delete axios.defaults.headers.common["Authorization"];
}

const AuthWrapper = () => {
  var token = localStorage.getItem('token');
  if(token) {
      try {
          const decode = JSON.parse(atob(token.split('.')[1]));
          // console.log(decode.exp * 1000);
          // console.log(new Date().getTime());
          if (decode.exp * 1000 < new Date().getTime()) {
                token = '';
                clearAuth();
                debugLog('Time Expired');
            }
      } catch (_) {
          // Unreadable payload - treat it as no session rather than throwing through render.
          token = '';
          clearAuth();
          debugLog('Malformed token');
      }
  }
  setAuthToken(token);
  return token
    ? <Outlet />
    : <Navigate to="/login" replace />;
};

class App extends Component {
  render() {
    return (
      <HashRouter>
        <VersionChecker />
        <HotAxleAlert/>
        <Suspense fallback={loading}>
          <Routes>
            <Route element={<AuthWrapper />}>
             <Route element={<ZoneGuard />}>
                <Route exact path="/division" name="Division" element={<Division />} />
                <Route exact path="/temp-setting" name="Temperature Setting" element={<TempSetting />} />
                <Route exact path="/user-setting" name="User Setting" element={<UserSetting />} />
                <Route exact path="/station" name="Station" element={<Station />} />
                <Route exact path="/sms-alert" name="SMS Alert" element={<SmsAlert />} />
                <Route exact path="/device" name="Device" element={<Device />} />
                <Route exact path="/alert-history" name="Alert History" element={<AlertHistory />} />
                <Route exact path="/zone" name="Zone" element={<Zone />} />
                <Route exact path="/alert-conf" name="Alert Config" element={<AlertColorConfig />} />
                <Route exact path="/habd-history" name="HABD History" element={<HabdHistory />} />
                <Route exact path="/device-reading" name="Device Reading" element={<DeviceReading />} />
                <Route exact path="/device-reading-device" name="Device Reading" element={<DeviceReadingDevice />} />
                <Route exact path="/table2" name="Search Table2" element={<SearchTable2 />} />
                <Route exact path="/device_status" name="Device Status" element={<DeviceStatus />} />
                <Route exact path="/reading-overview" name="Reading Overview" element={<Device2 />} />
                <Route exact path="/statistics-report" name="Statistics report" element={<ReadingCount />} />
                <Route exact path="/map-view" name="Map View" element={<MapView />} />
                <Route exact path="/contact" name="Contact" element={<Contact />} />
                <Route exact path="/maintenance" name="Maintenance" element={<Maintenance />} />
                <Route exact path="/device-details" name="Device Details" element={<DeviceDetails />} />
                <Route exact path="/status-history" name="Status-History" element={<StatusHistory />} />
                <Route exact path="/compatibility" name="Compatibility Support" element={<Comparability />} />
                <Route exact path="/device-fault" name="Device Fault" element={<DeviceFault/>} />  
                <Route exact path="/habd-details" name="HABD Machie Details" element={<HabdDetails/>} />  
                <Route exact path="/device-health" name="Device Health" element={<DeviceHealth/>} />
                <Route exact path="/breakdown-report" name="Breakdown Report" element={<BreakdownReport />} />
                <Route exact path="/alignment-recommend" name="Alignment Recommend" element={<AlignmentRecommend />} />
                <Route exact path="/device-command" name="Device Command" element={<DeviceCommand/>} />
                <Route exact path="/rule-engine" name="Rule Engine" element={<RuleEngine/>} />
                <Route exact path="/tickets" name="Tickets" element={<TicketsIndex />} />
                <Route exact path="/tickets/new" name="Raise Ticket" element={<TicketCreate />} />
                <Route exact path="/tickets/:id" name="Ticket Detail" element={<TicketDetail />} />
                <Route exact path="/tickets/:id/edit" name="Edit Ticket" element={<TicketEdit />} />
                <Route path="/tickets/analytics" element={<TicketAnalyticsLayout />}>
                  <Route index element={<TicketSymptomsReport />} />
                  <Route path="symptoms" element={<TicketSymptomsReport />} />
                  <Route path="parts" element={<TicketPartsReport />} />
                  <Route path="work-effectiveness" element={<TicketWorkEffectivenessReport />} />
                </Route>
             </Route>
            </Route>
            <Route exact path="/login" name="Login Page" element={<Login />} />
            <Route exact path="/register" name="Register Page" element={<Register />} />
            <Route exact path="/404" name="Page 404" element={<Page404 />} />
            <Route exact path="/500" name="Page 500" element={<Page500 />} />
            {/* <Route path="*" name="Home" element={<DefaultLayout />} /> */}
            <Route path="*" name="Login Page" element={<Login />} />
          </Routes>
        </Suspense>
      </HashRouter>
    )
  }
}

export default App
